import { computed, ref, Ref } from 'vue';
import { MONET_URL, TYPEFORM_URL } from '@/static/urls';
import { ComplexModal } from '@studyportals/modal';
import { DashboardAccessItem } from '@/models/dashboard-data.interface';
import { IRouter } from '@/models/router/router.interface';
import { ModalDashboardPreviewState, ModalDashboardPreviewStateType } from '@/models/modal.interface';
import DataTracker from '@/infrastructure/data-tracker';
import store from '@/store';

export default class DashboardPreviewModalCode {
	public carouselIndex: Ref<number> = ref(0);
	public modalContent: Ref<HTMLElement | null> = ref(null);
	private modal: Ref<ComplexModal | undefined> = ref(undefined);
	private readonly keyboardNavigationHandler: (event: KeyboardEvent) => void;

	public isModalStateOverview = computed((): boolean => {
		return this.modalDashboardPreviewState.value === ModalDashboardPreviewState.OVERVIEW;
	});

	public isModalStateCarousel = computed((): boolean => {
		return this.modalDashboardPreviewState.value === ModalDashboardPreviewState.CAROUSEL;
	});

	public amountOfCarouselSlides = computed((): number => {
		return this.relevantDashboard.value?.carouselImageCount || 0;
	});

	public dashboardName = computed((): string => {
		return this.relevantDashboard.value?.name || '';
	});

	public dashboardDescriptionParagraphs = computed(() : string[] => {
		return this.relevantDashboard.value?.descriptionLong || [];
	});

	public dashboardKeyResearchQuestions = computed(() : string[] => {
		return this.relevantDashboard.value?.keyResearchQuestions || [];
	});

	private relevantDashboard = computed((): DashboardAccessItem | undefined => {
		return this.availableDashboards.value.find((dashboard => dashboard.id === this.dashboardId.value));
	});

	private availableDashboards = computed((): DashboardAccessItem[] => {
		return store.getters.availableDashboards();
	});

	private dashboardId = computed((): string => {
		return this.router.value.props.id;
	});

	private router = computed((): IRouter => {
		return store.getters.router();
	});

	private modalDashboardPreviewState = computed((): ModalDashboardPreviewStateType => {
		return store.getters.modalDashboardPreviewState();
	});

	private tracker = computed((): DataTracker => {
		return store.getters.tracker();
	});

	constructor() {
		this.keyboardNavigationHandler = this.handleKeyboardNavigation.bind(this);
	}

	public mounted(): void {
		this.prepareModal();
		window.addEventListener('keydown', this.keyboardNavigationHandler);
	}

	public unmounted(): void {
		store.mutations.setRoutePropId('');
		window.removeEventListener('keydown', this.keyboardNavigationHandler);
		window.ModalManager.close(this.modal.value);
	}

	public retrieveCarouselImageUrl(index: number): string {
		const tag = this.relevantDashboard.value?.tag || '';
		return `${MONET_URL}preview_detailed_${tag}_${String(index + 1).padStart(2, '0')}.png`;
	}

	public openCarousel(): void {
		this.carouselIndex.value = 0;
		store.mutations.setModalDashboardPreviewState(ModalDashboardPreviewState.CAROUSEL);
	}

	public handleRequestDemoClick(): void {
		this.tracker.value.trackRequestDemoClick('dashboard_preview_modal');
		window.open(TYPEFORM_URL, '_blank')?.focus();
	}

	public handleKeyboardNavigation(event: KeyboardEvent): void {
		if (event.key === 'Escape' && this.shouldModalBeClosed()) {
			this.closeModal();
		}

		if (!this.isModalStateCarousel.value) {
			return;
		}

		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			this.moveCarouselBackward();
		} else if (event.key === 'ArrowRight') {
			event.preventDefault();
			this.moveCarouselForward();
		}
	}

	public moveCarouselForward(): void {
		this.moveCarouselToIndex(
			this.carouselIndex.value >= this.amountOfCarouselSlides.value - 1 ? 0 : this.carouselIndex.value + 1
		);
	}

	public moveCarouselBackward(): void {
		this.moveCarouselToIndex(
			this.carouselIndex.value <= 0 ? this.amountOfCarouselSlides.value - 1 : this.carouselIndex.value - 1
		);
	}

	public moveCarouselToIndex(index: number): void {
		if (index < 0 || index >= this.amountOfCarouselSlides.value) {
			return;
		}

		this.carouselIndex.value = index;
	}

	public shouldModalBeClosed(): boolean {
		if (this.isModalStateOverview.value) {
			return true;
		}

		store.mutations.setModalDashboardPreviewState(ModalDashboardPreviewState.OVERVIEW);
		return false;
	}

	private prepareModal(): void {
		if (!this.modalContent.value) {
			return;
		}

		this.modal.value = new ComplexModal(this.modalContent.value, {
			cssClassName: 'DashboardPreviewModalWrapper',
			destroyOnClose: true,
			onBeforeClose: this.shouldModalBeClosed.bind(this),
			onClose: this.closeModal.bind(this)
		});

		window.ModalManager.open(this.modal.value);
	}

	private closeModal(): void {
		store.mutations.setModalDashboardPreviewState(ModalDashboardPreviewState.INACTIVE);
	}
}
